// Firebase Visit Tracker Service
// Tracks website visits globally using Firebase Realtime Database

import { database, isFirebaseConfigured } from '@/lib/firebase';
import { ref, get, runTransaction, onValue, type Unsubscribe } from 'firebase/database';

interface VisitData {
  totalVisits: number;
  lastVisitTime: number;
  uniqueVisitors: number;
  lastUpdated: number;
}

// Cache for global visit count (updated by onValue)
let cachedGlobalVisits: number | null = null;
let lastFetchTime = 0;
let isUsingFirebase: boolean | null = null;

class FirebaseVisitTrackerService {
  private readonly STORAGE_KEY = 'invoiced_website_visits_firebase';
  private readonly TRACKED_SESSION_KEY = 'firebase_session_tracked';
  private unsubscribeVisits: Unsubscribe | null = null;

  private startRealtimeListener(): void {
    if (typeof window === 'undefined' || !isFirebaseConfigured() || !database || this.unsubscribeVisits) return;

    try {
      const visitsRef = ref(database, 'site_stats/total_visits');
      this.unsubscribeVisits = onValue(visitsRef, (snapshot) => {
        const val = snapshot.val();
        const visits = typeof val === 'number' ? val : 0;
        cachedGlobalVisits = visits;
        lastFetchTime = Date.now();
        isUsingFirebase = true;
        window.dispatchEvent(new CustomEvent('visits_updated'));
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.warn('[FirebaseTracker] Failed to start real-time listener:', msg);
    }
  }

  stopRealtimeListener(): void {
    if (this.unsubscribeVisits) {
      this.unsubscribeVisits();
      this.unsubscribeVisits = null;
    }
  }

  // Track a new visit
  async trackVisit(): Promise<void> {
    try {
      if (typeof window === 'undefined') return;

      if (!isFirebaseConfigured() || !database) {
        console.warn('[FirebaseTracker] Firebase not configured');
        return;
      }

      // Always start real-time listener (so returning visitors also get live visits)
      this.startRealtimeListener();

      const sessionTracked = sessionStorage.getItem(this.TRACKED_SESSION_KEY);
      if (sessionTracked) {
        return;
      }

      sessionStorage.setItem(this.TRACKED_SESSION_KEY, 'true');

      try {
        // Use transaction for atomic increment
        const visitsRef = ref(database, 'site_stats/total_visits');
        
        await runTransaction(visitsRef, (currentValue) => {
          return (currentValue || 0) + 1;
        });

        // Fetch updated count
        const snapshot = await get(visitsRef);
        const newCount = snapshot.val() || 0;
        
        cachedGlobalVisits = newCount;
        lastFetchTime = Date.now();
        isUsingFirebase = true;
        
        console.log('[FirebaseTracker] ✓ Visit tracked successfully, new GLOBAL count:', newCount);
        window.dispatchEvent(new CustomEvent('visits_updated'));
      } catch (error: any) {
        console.error('[FirebaseTracker] Failed to track visit:', error.message);
        throw error;
      }
    } catch (error: any) {
      console.error('[FirebaseTracker] Failed to track visit:', error.message || error);
    }
  }

  // Get total visits (real-time via onValue when Firebase configured)
  async getTotalVisitsAsync(): Promise<number> {
    if (!isFirebaseConfigured() || !database) {
      console.warn('[FirebaseTracker] Firebase not configured, using localStorage backup');
      isUsingFirebase = false;
      return this.loadLocalVisitData().totalVisits;
    }

    this.startRealtimeListener();

    // Use listener-backed cache when available (real-time)
    if (cachedGlobalVisits !== null && isUsingFirebase === true) {
      return cachedGlobalVisits;
    }

    try {
      const visitsRef = ref(database, 'site_stats/total_visits');
      const snapshot = await get(visitsRef);
      const visits = snapshot.val() || 0;

      cachedGlobalVisits = visits;
      lastFetchTime = Date.now();
      isUsingFirebase = true;

      return visits;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      console.warn('[FirebaseTracker] Failed to fetch visits:', msg);
      isUsingFirebase = false;
      return this.loadLocalVisitData().totalVisits;
    }
  }

  // Synchronous getter (uses cache; triggers async fetch if empty)
  getTotalVisits(): number {
    if (cachedGlobalVisits !== null) return cachedGlobalVisits;

    this.getTotalVisitsAsync().catch(console.error);
    return this.loadLocalVisitData().totalVisits;
  }

  // Check if using Firebase (global) or localStorage
  isUsingGlobalTracking(): boolean {
    return isUsingFirebase === true;
  }

  // Load local visit data (fallback)
  private loadLocalVisitData(): VisitData {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const stored = localStorage.getItem(this.STORAGE_KEY);
        if (stored) {
          return JSON.parse(stored);
        }
      }
    } catch (error) {
      console.error('[FirebaseTracker] Failed to load local visit data:', error);
    }
    return {
      totalVisits: 0,
      lastVisitTime: 0,
      uniqueVisitors: 0,
      lastUpdated: Date.now(),
    };
  }
}

export const firebaseVisitTracker = new FirebaseVisitTrackerService();
